import { useState } from 'react'
import SeoHead from '../components/SeoHead'
import { isPageSeoEnabled } from '../config/siteConfig'
import './Contact.css'

const Contact = () => {
  const seoEnabled = isPageSeoEnabled('contact')
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false)
      setSubmitted(true)
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        inquiryType: '',
        message: ''
      })
    }, 1500)
  }

  const inquiryTypes = [
    { value: '', label: 'Select Inquiry Type' },
    { value: 'sales', label: 'Sales Inquiry' },
    { value: 'partnership', label: 'Business Partnership' },
    { value: 'distribution', label: 'Distribution Inquiry' },
    { value: 'general', label: 'General Inquiry' },
    { value: 'careers', label: 'Career Opportunities' }
  ]

  return (
    <div className="contact-page">
      <SeoHead
        title="Contact SIBR Lifescience | Business Inquiries"
        description="Contact SIBR Lifescience for business inquiries, product information, and partnership opportunities. Reach us via email or visit our office."
        canonical="/contact"
        seoEnabled={seoEnabled}
      />
      
      {/* Hero */}
      <section className="page-hero contact-hero">
        <div className="container">
          <h1>Connect with SIBR Lifescience</h1>
          <p>
            Have questions about our products or interested in partnership opportunities? 
            We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information (NAP) */}
            <div className="contact-info">
              <h2>Contact Information</h2>
              <p className="contact-intro">
                Reach out to us through any of the following channels. Our team 
                is ready to assist you with your inquiries.
              </p>

              {/* NAP - Name, Address, Phone */}
              <div className="nap-section">
                {/* Registered Office */}
                <div className="nap-item">
                  <div className="nap-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                    </svg>
                  </div>
                  <div className="nap-content">
                    <h3>Registered At</h3>
                    <address>
                      <strong>SIBR Life Sciences Private Limited</strong><br />
                      Shanti Niketan, Shop 102/B Raj Complex,<br />
                      Satyanarayan Park Main Rd,<br />
                      Rajkot, Gujarat 360007<br />
                      India
                    </address>
                  </div>
                </div>

                {/* Manufacturing Unit */}
                <div className="nap-item">
                  <div className="nap-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div className="nap-content">
                    <h3>Manufactured At</h3>
                    <address>
                      Industrial Area, Baddi<br />
                      District Solan<br />
                      Himachal Pradesh - 173205<br />
                      India
                    </address>
                  </div>
                </div>

                <div className="nap-item">
                  <div className="nap-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                  </div>
                  <div className="nap-content">
                    <h3>Phone</h3>
                    <p>+91 XXXXX XXXXX (Sales)</p>
                    <p>+91 XXXXX XXXXX (General)</p>
                  </div>
                </div>

                <div className="nap-item">
                  <div className="nap-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                  </div>
                  <div className="nap-content">
                    <h3>Email</h3>
                    <p>
                      <a href="mailto:info@sibrlifescience.com">info@sibrlifescience.com</a> (General)
                    </p>
                    <p>
                      <a href="mailto:sales@sibrlifescience.com">sales@sibrlifescience.com</a> (Sales)
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="business-hours">
                <h3>Business Hours</h3>
                <p>Monday - Saturday: 9:00 AM - 6:00 PM IST</p>
                <p>Sunday: Closed</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-section">
              {submitted ? (
                <div className="form-success">
                  <div className="success-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <h3>Thank You!</h3>
                  <p>
                    Your message has been received. Our team will review your inquiry 
                    and get back to you within 1-2 business days.
                  </p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setSubmitted(false)}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <h2>Business Inquiry Form</h2>
                  <p className="form-intro">
                    Fill out the form below and we'll get back to you as soon as possible.
                  </p>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="company">Company Name</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="inquiryType">Inquiry Type *</label>
                    <select
                      id="inquiryType"
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleChange}
                      required
                    >
                      {inquiryTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Please describe your inquiry..."
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    className="btn btn-primary btn-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                        </svg>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2>Our Location</h2>
          <div className="map-wrapper">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.7446071234567!2d70.767889!3d22.3015292!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3959c9123d34c563%3A0x48734b6a0b759964!2sSIBR%20Life%20Science%20Private%20Limited!5e0!3m2!1sen!2sin!4v1702345678901!5m2!1sen!2sin"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="SIBR Life Science Private Limited - Rajkot"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="quick-links-section section-sm bg-light">
        <div className="container">
          <div className="quick-links-content">
            <h3>Looking for Our Products?</h3>
            <p>Find SIBR Lifescience products on leading healthcare platforms.</p>
            <a 
              href="https://www.1mg.com/marketer/sibr-life-sciences-private-limited-84382"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Visit Our 1mg Store
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
